import * as React from 'react'
import { useState } from 'react'

import { Box, Colors, InfoDot, Text, colors } from '@factorialco/deprecated-design-system'
import { CategoryBarChart } from '@factorialco/f0-react'
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Spinner,
  Widget,
} from '@factorialco/f0-react/dist/experimental'
import { endOfDay, endOfQuarter, startOfDay, startOfQuarter } from 'date-fns'
import {
  ExpensesExpensableFiltersInput,
  ExpensesReportsPageDocument,
} from 'generated/resources/graphql'

import { DateNavigator } from 'modules/core/components/DateNavigator'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useEffect, useUnsafeEffect } from 'modules/core/hooks/useEffect'
import { useQuery } from 'modules/core/hooks/useQuery'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { fromCents } from 'modules/core/lib/formatCurrencyUtils'
import i18n from 'modules/core/lib/i18n'

import { usePageDateFilters } from 'modules/expenses/lib/PageDateFiltersContext'
import { FinancialWorkspacePromotionBanner } from 'modules/finance/components/FinancialWorkspace/components/PromotionBanner'
import { ResponsivePage } from 'modules/layout/components/ResponsivePage'

import ExpensablesByCategoryDatatable from './ExpensablesByCategoryDatatable'
import ExpensablesByMonthChart from './ExpensablesByMonthChart'
import ExpensablesByTeamDatatable from './ExpensablesByTeamDatatable'
import ExpenseAmountByStatusTable from './ExpenseAmountByStatusTable'
import ExpensesByCostCenterDatatable from './ExpensesByCostCenterDatatable'
import ExpensesByEmployeeDatatable from './ExpensesByEmployeeDatatable'
import CurrencyFilter from './Filters/Currency'
import EmployeeFilter from './Filters/Employee'
import ExpensesTypeFilter from './Filters/ExpensesType'
import LegalEntityFilter from './Filters/LegalEntity'
import ProjectsFilter from './Filters/Projects'
import Money from './Money'
import PeriodBalanceTag from './PeriodBalanceTag'
import { usePersistedFilters } from './hooks/usePersistedFilters'
import { useReportNavigation } from './hooks/useReportNavigation'
import { useTrackDateFilterChange } from './hooks/useTrackDateFilterChange'
import * as styles from './index.vanilla.css'
import { calculatePreviousPeriod } from './lib/calculatePreviousPeriod'

interface ReportValuePanelProps {
  title: string
  amount: number
  total: number
  partial?: number
  color?: Colors
  currentPeriodAmount: number
  lastPeriodAmount: number
  currency: string
}

const ReportValuePanel: React.FC<ReportValuePanelProps> = ({
  title,
  amount,
  total,
  partial,
  color,
  currentPeriodAmount,
  lastPeriodAmount,
  currency,
}) => {
  return (
    <Box gap="s8">
      <Box flexDirection="row" gap="s8" alignItems="center">
        {color && <InfoDot color={color} size="small" />}
        <Text.Normal color="grey1000" weight="medium" size="200">
          {title}
        </Text.Normal>
      </Box>
      <Box flexDirection="row" gap="s8" alignItems="center">
        <Text.Header1>
          <Money amount={amount} currency={currency} />
        </Text.Header1>
        <PeriodBalanceTag
          currentPeriodAmount={currentPeriodAmount}
          lastPeriodAmount={lastPeriodAmount}
        />
      </Box>
      <Box paddingBottom="s4" paddingTop="s2">
        <Text.Normal size="200">
          {i18n.tp('expenses_v2.reports.expenses_by_approval_status.common.expenses_numeric', {
            count: total,
          })}
          {partial !== undefined &&
            ' ' +
              i18n.t(
                'expenses_v2.reports.expenses_by_approval_status.common.partially_reimbursed',
                {
                  partial: partial,
                }
              )}
        </Text.Normal>
      </Box>
    </Box>
  )
}

interface ExpensesReportsPageProps {}

const DEFAULT_TIMEZONE = 'UTC'

const getTimezone = (): string => {
  try {
    if (typeof Intl === 'undefined' || !Intl.DateTimeFormat) {
      return DEFAULT_TIMEZONE
    }
    return Intl.DateTimeFormat().resolvedOptions().timeZone ?? DEFAULT_TIMEZONE
  } catch {
    return DEFAULT_TIMEZONE
  }
}

const ExpensesReportsPage: React.FC<ExpensesReportsPageProps> = () => {
  const { company } = useCurrentSession()

  const { dateFilters, setPageDateFilters } = usePageDateFilters()
  const { buildExpensesListUrl } = useReportNavigation()

  const {
    filters,
    setFilteredEmployees,
    setFilteredLegalEntities,
    setFilteredCreationType,
    setFilteredCurrency,
    setInitialCurrency,
    setFilteredProjects,
  } = usePersistedFilters()

  const filtersInput: ExpensesExpensableFiltersInput = {
    currency: filters.currency,
    employeeIds: filters.employees.map((employee) => employee.id),
    legalEntityIds: filters.legalEntities.map((legalEntity) => legalEntity.id),
    creationType: filters.creationType,
    projectIds: filters.projects?.map((project) => project.id) ?? [],
  }

  const [isInitialLoad, setIsInitialLoad] = useState(true)

  const currentPeriod = {
    startDate: startOfDay(dateFilters.startDate),
    endDate: endOfDay(dateFilters.endDate),
  }
  const previousPeriod = currentPeriod
    ? calculatePreviousPeriod(currentPeriod.startDate, currentPeriod.endDate)
    : null

  const { data, loading, error } = useQuery(ExpensesReportsPageDocument, {
    variables: {
      companyId: company.id,
      from: currentPeriod?.startDate.toISOString() ?? '',
      to: currentPeriod?.endDate.toISOString() ?? '',
      localTz: getTimezone(),
      previousPeriodFrom: previousPeriod?.startDate.toString() ?? '',
      previousPeriodTo: previousPeriod?.endDate.toString() ?? '',
      employeeIds: filtersInput.employeeIds ?? [],
      currency: filtersInput.currency,
      legalEntityIds: filtersInput.legalEntityIds ?? [],
      creationType: filtersInput.creationType,
      projectIds: filtersInput.projectIds ?? [],
    },
    skip: currentPeriod == null,
    onCompleted: () => {
      setIsInitialLoad(false)
    },
  })

  const partials = {
    pending: {
      reimbursableCount:
        (data?.expenses.currentPeriod.nodes[0]?.pending.reimbursableCount ?? 0) -
        (data?.expenses.currentPeriod.nodes[0]?.pending.fullyReimbursableCount ?? 0),
    },
    approved: {
      reimbursableCount:
        (data?.expenses.currentPeriod.nodes[0]?.approved.reimbursableCount ?? 0) -
        (data?.expenses.currentPeriod.nodes[0]?.approved.fullyReimbursableCount ?? 0),
    },
  }

  const multicurrencyCallout =
    (data?.expenses.currentPeriod.nodes[0]?.totalCountIncludingMismatches ?? 0) >
    (data?.expenses.currentPeriod.nodes[0]?.totalCount ?? 0)

  const mainLegalEntityCurrency = data?.apiCore.currents[0]?.company.legalEntities.find(
    (legalEntity) => legalEntity.main
  )?.currency

  useUnsafeEffect(() => {
    if (mainLegalEntityCurrency) {
      setInitialCurrency(mainLegalEntityCurrency)
    }
  }, [mainLegalEntityCurrency, setInitialCurrency])

  useTrackDateFilterChange(currentPeriod)
  useEffect(() => {
    track(trackingEvents.expenses.expensesReportLoad)
  }, [])

  const handleEmptyStateClick = () => {
    const now = new Date()
    const startDate = startOfQuarter(now)
    const endDate = endOfQuarter(now)
    setPageDateFilters(startDate, endDate)
  }

  return (
    <ResponsivePage.Body loading={isInitialLoad} variant="full" error={error} authorized>
      <Box width="full" gap="s24" paddingBottom="s48">
        <FinancialWorkspacePromotionBanner source="expenses_reports_tab" />
        <Box flexDirection="row" gap="s12" width="full">
          <Box flexDirection="row" gap="s12" width="full">
            <CurrencyFilter
              options={data?.apiCore.currenciesConnection.edges.map((edge) => edge.node) || []}
              selected={filters.currency}
              setSelected={(newValue) => {
                track(trackingEvents.expenses.filter, {
                  origin: 'expenses_reports',
                  filter_by: 'currency',
                })
                setFilteredCurrency(newValue)
              }}
            />
            <LegalEntityFilter
              selected={filters.legalEntities}
              setSelected={(newValue) => {
                track(trackingEvents.expenses.filter, {
                  origin: 'expenses_reports',
                  filter_by: 'legal_entity',
                })
                setFilteredLegalEntities(newValue)
              }}
            />
            <ExpensesTypeFilter
              selected={filters.creationType}
              setSelected={(newValue) => {
                track(trackingEvents.expenses.filter, {
                  origin: 'expenses_reports',
                  filter_by: 'expense_type',
                })
                setFilteredCreationType(newValue)
              }}
            />
            <EmployeeFilter
              selected={filters.employees}
              setSelected={(newValue) => {
                track(trackingEvents.expenses.filter, {
                  origin: 'expenses_reports',
                  filter_by: 'employee',
                })
                setFilteredEmployees(newValue)
              }}
            />
            <ProjectsFilter
              selected={filters.projects}
              setSelected={(newValue) => {
                track(trackingEvents.expenses.filter, {
                  origin: 'expenses_reports',
                  filter_by: 'project',
                })
                setFilteredProjects(newValue)
              }}
            />
            {loading && <Spinner />}
          </Box>
          <Box width="s600" alignItems="flexEnd">
            <DateNavigator
              startDate={dateFilters.startDate}
              endDate={dateFilters.endDate}
              onDateRangeChange={setPageDateFilters}
            />
          </Box>
        </Box>

        {multicurrencyCallout && (
          <Alert variant="info">
            <AlertTitle>{i18n.t('expenses_v2.reports.alerts.multicurrency.title')}</AlertTitle>
            <AlertDescription>
              {i18n.t('expenses_v2.reports.alerts.multicurrency.description')}
            </AlertDescription>
          </Alert>
        )}
        <Box flexDirection="row" gap="s24" width="full">
          <Box width="full">
            <Widget
              header={{
                title: i18n.t('expenses_v2.reports.expenses_by_approval_status.pending.title'),
                link: {
                  title: i18n.t('expenses_v2.reports.expenses_by_approval_status.pending.title'),
                  url: buildExpensesListUrl({
                    filters: filtersInput,
                    status: 'pending',
                    startDate: currentPeriod?.startDate,
                    endDate: currentPeriod?.endDate,
                  }),
                  onClick: () => {
                    track(trackingEvents.expenses.expensesReportWidgetNavigation, {
                      widget: 'pending_expenses',
                    })
                  },
                },
              }}
            >
              <Box width="full" gap="s24">
                <Box width="full" gap="s8">
                  <Text.Header1>
                    <Money
                      amount={data?.expenses.currentPeriod.nodes[0]?.pending.totalAmount ?? 0}
                      currency={filters.currency}
                    />
                  </Text.Header1>
                  <CategoryBarChart
                    data={[
                      {
                        name: i18n.t(
                          'expenses_v2.reports.expenses_by_approval_status.pending.columns.reimbursable'
                        ),
                        value: fromCents(
                          data?.expenses.currentPeriod.nodes[0]?.pending.reimbursableAmount ?? 0
                        ),
                        color: colors.viridian1000,
                      },
                      {
                        name: i18n.t(
                          'expenses_v2.reports.expenses_by_approval_status.pending.columns.non_reimbursable'
                        ),
                        value: fromCents(
                          data?.expenses.currentPeriod.nodes[0]?.pending.nonReimbursableAmount ?? 0
                        ),
                        color: colors.tangerine1000,
                      },
                    ]}
                    legend={false}
                  />
                </Box>
                <Box width="full" paddingY="s4">
                  <ReportValuePanel
                    title={i18n.t(
                      'expenses_v2.reports.expenses_by_approval_status.pending.columns.reimbursable'
                    )}
                    color="viridian1000"
                    amount={data?.expenses.currentPeriod.nodes[0]?.pending.reimbursableAmount ?? 0}
                    total={
                      data?.expenses.currentPeriod.nodes[0]?.pending.fullyReimbursableCount ?? 0
                    }
                    partial={partials.pending.reimbursableCount}
                    currentPeriodAmount={
                      data?.expenses.currentPeriod.nodes[0]?.pending.reimbursableAmount ?? 0
                    }
                    lastPeriodAmount={
                      data?.expenses.previousPeriod.nodes[0]?.pending.reimbursableAmount ?? 0
                    }
                    currency={filters.currency}
                  />
                </Box>
                <Box width="full" paddingY="s4">
                  <ReportValuePanel
                    title={i18n.t(
                      'expenses_v2.reports.expenses_by_approval_status.pending.columns.non_reimbursable'
                    )}
                    color="tangerine1000"
                    amount={
                      data?.expenses.currentPeriod.nodes[0]?.pending.nonReimbursableAmount ?? 0
                    }
                    total={
                      data?.expenses.currentPeriod.nodes[0]?.pending.fullyNonReimbursableCount ?? 0
                    }
                    partial={partials.pending.reimbursableCount}
                    currentPeriodAmount={
                      data?.expenses.currentPeriod.nodes[0]?.pending.nonReimbursableAmount ?? 0
                    }
                    lastPeriodAmount={
                      data?.expenses.previousPeriod.nodes[0]?.pending.nonReimbursableAmount ?? 0
                    }
                    currency={filters.currency}
                  />
                </Box>
              </Box>
            </Widget>
          </Box>
          <Box width="full">
            <Widget
              header={{
                title: i18n.t('expenses_v2.reports.expenses_by_approval_status.approved.title'),
                link: {
                  title: i18n.t('expenses_v2.reports.expenses_by_approval_status.approved.title'),
                  url: buildExpensesListUrl({
                    filters: filtersInput,
                    status: 'approved',
                    startDate: currentPeriod?.startDate,
                    endDate: currentPeriod?.endDate,
                  }),
                  onClick: () => {
                    track(trackingEvents.expenses.expensesReportWidgetNavigation, {
                      widget: 'approved_expenses',
                    })
                  },
                },
              }}
            >
              <Box width="full" gap="s24">
                <Box width="full" gap="s8">
                  <Text.Header1>
                    <Money
                      amount={data?.expenses.currentPeriod.nodes[0]?.approved.totalAmount ?? 0}
                      currency={filters.currency}
                    />
                  </Text.Header1>
                  <CategoryBarChart
                    data={[
                      {
                        name: i18n.t(
                          'expenses_v2.reports.expenses_by_approval_status.approved.columns.reimbursable'
                        ),
                        value: fromCents(
                          data?.expenses.currentPeriod.nodes[0]?.approved.reimbursableAmount ?? 0
                        ),
                        color: colors.viridian1000,
                      },
                      {
                        name: i18n.t(
                          'expenses_v2.reports.expenses_by_approval_status.approved.columns.non_reimbursable'
                        ),
                        value: fromCents(
                          data?.expenses.currentPeriod.nodes[0]?.approved.nonReimbursableAmount ?? 0
                        ),
                        color: colors.tangerine1000,
                      },
                    ]}
                    legend={false}
                  />
                </Box>

                <Box width="full" paddingY="s4">
                  <ReportValuePanel
                    title={i18n.t(
                      'expenses_v2.reports.expenses_by_approval_status.approved.columns.reimbursable'
                    )}
                    color="viridian1000"
                    amount={data?.expenses.currentPeriod.nodes[0]?.approved.reimbursableAmount ?? 0}
                    total={data?.expenses.currentPeriod.nodes[0]?.approved.reimbursableCount ?? 0}
                    partial={partials.approved.reimbursableCount}
                    currentPeriodAmount={
                      data?.expenses.currentPeriod.nodes[0]?.approved.reimbursableAmount ?? 0
                    }
                    lastPeriodAmount={
                      data?.expenses.previousPeriod.nodes[0]?.approved.reimbursableAmount ?? 0
                    }
                    currency={filters.currency}
                  />
                </Box>
                <Box width="full" paddingY="s4">
                  <ReportValuePanel
                    title={i18n.t(
                      'expenses_v2.reports.expenses_by_approval_status.approved.columns.non_reimbursable'
                    )}
                    color="tangerine1000"
                    amount={
                      data?.expenses.currentPeriod.nodes[0]?.approved.nonReimbursableAmount ?? 0
                    }
                    total={
                      data?.expenses.currentPeriod.nodes[0]?.approved.nonReimbursableCount ?? 0
                    }
                    partial={partials.approved.reimbursableCount}
                    currentPeriodAmount={
                      data?.expenses.currentPeriod.nodes[0]?.approved.nonReimbursableAmount ?? 0
                    }
                    lastPeriodAmount={
                      data?.expenses.previousPeriod.nodes[0]?.approved.nonReimbursableAmount ?? 0
                    }
                    currency={filters.currency}
                  />
                </Box>
              </Box>
            </Widget>
          </Box>
        </Box>
        <Box gap="s8" width="full" paddingTop="s12">
          <Box paddingLeft="s6" paddingBottom="s4">
            <Text.Normal color="grey1000" weight="medium" size="300">
              {i18n.t('expenses_v2.reports.status_breakdown.title')}
            </Text.Normal>
          </Box>
          <Box flexDirection="row" width="full" gap="s24">
            <Box style={{ flexGrow: '2', flexBasis: '0' }}>
              <Widget
                header={{
                  title: i18n.t('expenses_v2.reports.expenses_status_funnel.title'),
                }}
                fullHeight
              >
                <Box width="full" className={styles.chartContainer}>
                  <ExpenseAmountByStatusTable
                    currentPeriod={data?.expenses.currentPeriod.nodes[0]}
                    currency={filters.currency}
                  />
                </Box>
              </Widget>
            </Box>
            <Box
              gap="s24"
              style={{ flexGrow: '1', flexBasis: '0', maxWidth: '460px', minWidth: '250px' }}
            >
              <Widget
                header={{
                  title: i18n.t('expenses_v2.reports.requested_changes.title'),
                  link: {
                    title: i18n.t('expenses_v2.reports.requested_changes.title'),
                    url: buildExpensesListUrl({
                      filters: filtersInput,
                      status: 'changes_requested',
                      startDate: currentPeriod?.startDate,
                      endDate: currentPeriod?.endDate,
                    }),
                    onClick: () => {
                      track(trackingEvents.expenses.expensesReportWidgetNavigation, {
                        widget: 'requested_changes',
                      })
                    },
                  },
                }}
              >
                <ReportValuePanel
                  title=""
                  amount={data?.expenses.currentPeriod.nodes[0]?.waitingForChanges.totalAmount ?? 0}
                  total={data?.expenses.currentPeriod.nodes[0]?.waitingForChanges.totalCount ?? 0}
                  currentPeriodAmount={
                    data?.expenses.currentPeriod.nodes[0]?.waitingForChanges.totalAmount ?? 0
                  }
                  lastPeriodAmount={
                    data?.expenses.previousPeriod.nodes[0]?.waitingForChanges.totalAmount ?? 0
                  }
                  currency={filters.currency}
                />
              </Widget>
              <Widget
                header={{
                  title: i18n.t('expenses_v2.reports.rejected.title'),
                  link: {
                    title: i18n.t('expenses_v2.reports.rejected.title'),
                    url: buildExpensesListUrl({
                      filters: filtersInput,
                      status: 'rejected',
                      startDate: currentPeriod?.startDate,
                      endDate: currentPeriod?.endDate,
                    }),
                    onClick: () => {
                      track(trackingEvents.expenses.expensesReportWidgetNavigation, {
                        widget: 'rejected_expenses',
                      })
                    },
                  },
                }}
              >
                <ReportValuePanel
                  title=""
                  amount={data?.expenses.currentPeriod.nodes[0]?.rejected.totalAmount ?? 0}
                  total={data?.expenses.currentPeriod.nodes[0]?.rejected.totalCount ?? 0}
                  currentPeriodAmount={
                    data?.expenses.currentPeriod.nodes[0]?.rejected.totalAmount ?? 0
                  }
                  lastPeriodAmount={
                    data?.expenses.previousPeriod.nodes[0]?.rejected.totalAmount ?? 0
                  }
                  currency={filters.currency}
                />
              </Widget>
            </Box>
          </Box>
        </Box>
        <Box flexDirection="row" gap="s24" width="full">
          <Box width="full">
            <Widget
              header={{
                title: i18n.t('expenses_v2.reports.expenses_by_category.title'),
              }}
            >
              <ExpensablesByCategoryDatatable filters={filtersInput} />
            </Widget>
          </Box>
          <Box width="full">
            <Widget
              header={{
                title: i18n.t('expenses_v2.reports.expenses_by_employee.title'),
              }}
            >
              <ExpensesByEmployeeDatatable filters={filtersInput} />
            </Widget>
          </Box>
        </Box>
        <Box flexDirection="row" gap="s24" width="full">
          <Box width="full">
            <Widget
              header={{
                title: i18n.t('expenses_v2.reports.expenses_by_team.title'),
                info: i18n.t('expenses_v2.reports.expenses_by_team.tooltip'),
              }}
            >
              <ExpensablesByTeamDatatable filters={filtersInput} />
            </Widget>
          </Box>
          <Box width="full">
            <Widget
              header={{
                title: i18n.t('expenses_v2.reports.expenses_by_cost_center.title'),
                info: i18n.t('expenses_v2.reports.expenses_by_cost_center.tooltip'),
              }}
            >
              <ExpensesByCostCenterDatatable filters={filtersInput} />
            </Widget>
          </Box>
        </Box>
        <Box flexDirection="row" width="full">
          <Box width="full">
            <Widget
              header={{
                title: i18n.t('expenses_v2.reports.expenses_by_month.title'),
              }}
            >
              <Box paddingTop="s24">
                <ExpensablesByMonthChart
                  data={data}
                  currency={filters.currency}
                  onEmptyStateClick={handleEmptyStateClick}
                />
              </Box>
            </Widget>
          </Box>
        </Box>
      </Box>
    </ResponsivePage.Body>
  )
}

export default ExpensesReportsPage
